import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import SchedulePicker from "./SchedulePicker";
import { AtsOption, CreateScrapeConfig } from "../../Types/Scrapes";
import { useAllClientsQuery } from "../../Queries/ClientManagementQueries";
import { Client } from "../../Types/ClientManagement";

const useStyles = makeStyles(() => ({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 400,
    paddingTop: "8px",
  },
}));

interface CreateScrapeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateScrapeConfig) => void;
  atsOptions: AtsOption[];
  isSubmitting: boolean;
}

export default function CreateScrapeModal({
  open,
  onClose,
  onSubmit,
  atsOptions,
  isSubmitting,
}: CreateScrapeModalProps) {
  const classes = useStyles();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [atsId, setAtsId] = useState("");
  const [scrapeName, setScrapeName] = useState("");
  const [url, setUrl] = useState("");
  const [cronExpression, setCronExpression] = useState("0 2 * * *");

  const { data: clients = [] } = useAllClientsQuery(open);

  const sortedClients = [...clients].sort((a, b) => (a.shortName || "").localeCompare(b.shortName || ""));

  const canSubmit = selectedClient && atsId && url && cronExpression;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      clientId: selectedClient.id,
      atsId,
      scrapeName: scrapeName || undefined,
      url,
      cronExpression,
    });
  };

  const handleClose = () => {
    setSelectedClient(null);
    setAtsId("");
    setScrapeName("");
    setUrl("");
    setCronExpression("0 2 * * *");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Scrape Configuration</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Autocomplete
            options={sortedClients}
            getOptionLabel={(option) => option.shortName || option.name || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            value={selectedClient}
            onChange={(_e, value) => setSelectedClient(value)}
            renderInput={(params) => (
              <TextField {...params} label="Client" variant="outlined" size="small" required />
            )}
          />
          <FormControl variant="outlined" size="small" required>
            <InputLabel>ATS</InputLabel>
            <Select value={atsId} onChange={(e) => setAtsId(e.target.value as string)} label="ATS">
              {atsOptions.map((ats) => (
                <MenuItem key={ats.id} value={ats.id}>
                  {ats.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Scrape Name"
            value={scrapeName}
            onChange={(e) => setScrapeName(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Optional"
          />
          <TextField
            label="Job Board URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            variant="outlined"
            size="small"
            required
            placeholder="https://careers.example.com/jobs"
          />
          <SchedulePicker value={cronExpression} onChange={setCronExpression} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
